import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { paymentService } from '../services/payments';
import { CheckoutPlan, Payment, PaymentStatus } from '../types/payment';
import { MembershipType } from '../types/member';
import { Container, Stack } from '../design-system/components/Layout';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { Badge } from '../design-system/components/Decorative';
import { PageHeader } from '../design-system/patterns/PageHeader';
import SearchBar from '../components/common/SearchBar';
import { DataTable, Column } from '../components/common/DataTable';
import { EmptyState } from '../components/common/EmptyState';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { CreditCardIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { redirectToXpayCheckout } from '../utils/xpayCheckout';

const PaymentsContainer = styled(Container)`
  max-width: 1200px;
`;

const CardCentered = styled(Card)`
  text-align: center;
  padding: ${spacing[12]};
`;

const TextWithMargin = styled(Text)`
  margin-bottom: ${spacing[4]};
`;

const FiltersSection = styled(Card)`
  padding: ${spacing[6]};
  margin-bottom: ${spacing[6]};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing[4]};
  align-items: end;
`;

const FilterLabel = styled.label`
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
`;

const StatusBadge = styled(Badge)<{ status: PaymentStatus }>`
  background: ${props => {
    switch (props.status) {
      case PaymentStatus.Paid:
        return props.theme.colors.success;
      case PaymentStatus.Overdue:
        return props.theme.colors.error;
      case PaymentStatus.Cancelled:
        return props.theme.colors.textMuted;
      default:
        return props.theme.colors.warning;
    }
  }};
  color: white;
`;

const PAGE_SIZE = 25;

/** API may return enum as string (e.g. "Paid", "Regular"); normalize to numeric for labels and receipt button. */
function normalizePayment(p: Payment & { status?: PaymentStatus | string; membershipType?: MembershipType | string }): Payment {
  const status =
    typeof p.status === 'number'
      ? p.status
      : (PaymentStatus as unknown as Record<string, number>)[p.status as string] ?? PaymentStatus.Pending;
  const membershipType =
    typeof p.membershipType === 'number'
      ? p.membershipType
      : (MembershipType as unknown as Record<string, number>)[p.membershipType as string] ?? MembershipType.Regular;
  return { ...p, status, membershipType };
}

const Payments: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [plans, setPlans] = useState<Record<string, CheckoutPlan>>({});
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const loadPayments = async () => {
      setLoading(true);
      try {
        const data = await paymentService.getMyPayments();
        setPayments(data.map(normalizePayment));
      } catch (error) {
        console.error('Error loading payments:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadPayments();
    }
  }, [user]);

  useEffect(() => {
    const open = payments.filter(
      payment => payment.status === PaymentStatus.Pending || payment.status === PaymentStatus.Overdue
    );
    if (open.length === 0) {
      setPlans({});
      return;
    }
    let cancelled = false;
    Promise.all(
      open.map(async payment => {
        try {
          const plan = await paymentService.getCheckoutPlan(payment.id);
          return [payment.id, plan] as const;
        } catch {
          return null;
        }
      })
    ).then(rows => {
      if (cancelled) return;
      const next: Record<string, CheckoutPlan> = {};
      rows.forEach(row => {
        if (row) next[row[0]] = row[1];
      });
      setPlans(next);
    });
    return () => {
      cancelled = true;
    };
  }, [payments]);

  const filteredPayments = payments.filter((payment) => {
    if (debouncedSearch.trim()) {
      const searchLower = debouncedSearch.toLowerCase();
      return (
        payment.year.toString().includes(searchLower) ||
        payment.amount.toString().includes(searchLower) ||
        payment.memberName.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const total = filteredPayments.length;
  const paginatedData = filteredPayments.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const getStatusLabel = (status: PaymentStatus): string => {
    switch (status) {
      case PaymentStatus.Pending:
        return t('payments.status.pending');
      case PaymentStatus.Paid:
        return t('payments.status.paid');
      case PaymentStatus.Overdue:
        return t('payments.status.overdue');
      case PaymentStatus.Cancelled:
        return t('payments.status.cancelled');
      default:
        return t('payments.status.unknown');
    }
  };

  const getMembershipTypeLabel = (type: MembershipType): string => {
    switch (type) {
      case MembershipType.Regular:
        return t('membership.regular');
      case MembershipType.Student:
        return t('membership.student');
      case MembershipType.Retired:
        return t('membership.retired');
      case MembershipType.Honorary:
        return t('membership.honorary');
      default:
        return 'Unknown';
    }
  };

  const handleDownloadReceipt = async (paymentId: string) => {
    try {
      const blob = await paymentService.downloadReceipt(paymentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt_${payments.find(p => p.id === paymentId)?.year}_${paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert(t('payments.downloadError'));
    }
  };

  const handlePayNow = async (paymentId: string) => {
    try {
      setPayingId(paymentId);
      await redirectToXpayCheckout(paymentId);
    } catch (error) {
      setPayingId(null);
      console.error('Checkout failed:', error);
      alert(error instanceof Error ? error.message : t('payments.checkoutError'));
    }
  };

  const formatCurrency = (amount: number): string => {
    return `€${amount.toFixed(2)}`;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  const paymentColumns: Column<Payment>[] = [
    {
      key: 'year',
      header: t('payments.year'),
      render: (payment) => payment.year,
    },
    {
      key: 'amount',
      header: t('payments.amount'),
      render: (payment) => formatCurrency(payment.amount),
    },
    {
      key: 'membershipType',
      header: t('payments.membershipType'),
      render: (payment) => getMembershipTypeLabel(payment.membershipType),
    },
    {
      key: 'status',
      header: t('payments.statusLabel') || t('payments.status'),
      render: (payment) => (
        <StatusBadge status={payment.status} size="sm" shape="pill">
          {getStatusLabel(payment.status)}
        </StatusBadge>
      ),
    },
    {
      key: 'paidAt',
      header: t('payments.paidAt'),
      render: (payment) => formatDate(payment.paidAt),
    },
    {
      key: 'actions',
      header: t('admin.actions'),
      actions: (payment) =>
        payment.status === PaymentStatus.Paid ? (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadReceipt(payment.id);
            }}
          >
            <DocumentArrowDownIcon style={{ width: 16, height: 16, marginRight: spacing[2] }} />
            {t('payments.downloadReceipt')}
          </Button>
        ) : payment.status === PaymentStatus.Pending || payment.status === PaymentStatus.Overdue ? (
          <Button
            variant="primary"
            size="sm"
            disabled={payingId === payment.id}
            onClick={(e) => {
              e.stopPropagation();
              handlePayNow(payment.id);
            }}
          >
            <CreditCardIcon style={{ width: 16, height: 16, marginRight: spacing[2] }} />
            {payingId === payment.id
              ? t('payments.checkoutStarting')
              : plans[payment.id]?.isBundled
                ? t('payments.payBundled', { years: (plans[payment.id]?.years ?? []).join(' + ') })
                : t('payments.payNow')}
          </Button>
        ) : null,
    },
  ];

  if (loading) {
    return (
      <PaymentsContainer>
        <PageHeader title={t('payments.title')} pattern={true} />
        <CardCentered variant="filled">
          <LoadingSpinner size="lg" />
          <TextWithMargin size="lg" color="secondary">
            {t('common.loading')}
          </TextWithMargin>
        </CardCentered>
      </PaymentsContainer>
    );
  }

  if (payments.length === 0) {
    return (
      <PaymentsContainer>
        <Stack direction="column" spacing="xl">
          <PageHeader title={t('payments.title')} pattern={true} />
          <EmptyState
            icon="document"
            title={t('payments.noPayments')}
            text={t('payments.noPaymentsText')}
          />
        </Stack>
      </PaymentsContainer>
    );
  }

  return (
    <PaymentsContainer>
      <Stack direction="column" spacing="xl">
        <PageHeader
          title={t('payments.title')}
          subtitle={
            !loading && total > 0
              ? t('admin.showingResults', {
                  start: (page - 1) * PAGE_SIZE + 1,
                  end: Math.min(page * PAGE_SIZE, total),
                  total,
                }) || `Showing ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, total)} of ${total}`
              : undefined
          }
          pattern={true}
        />

        {Object.values(plans).some(plan => plan.isBundled) && (
          <FiltersSection variant="elevated" accentBorder="top">
            {Object.values(plans)
              .filter(plan => plan.isBundled)
              .slice(0, 1)
              .map(plan => (
                <Text key={plan.paymentId} size="sm">
                  {t('payments.arrearsNotice', {
                    years: plan.years.join(' + '),
                    amount: plan.totalAmount.toFixed(2),
                  })}
                </Text>
              ))}
          </FiltersSection>
        )}

        {payments.length > 0 && (
          <FiltersSection variant="elevated" accentBorder="top">
            <Stack direction="column" spacing="md">
              <Heading variant="h3" color="text">
                {t('admin.filters')}
              </Heading>
              <FiltersGrid>
                <Stack direction="column" spacing="sm">
                  <FilterLabel>{t('payments.searchPlaceholder')}</FilterLabel>
                  <SearchBar
                    value={search}
                    onChange={setSearch}
                    onSearch={setDebouncedSearch}
                    placeholder={t('payments.searchPlaceholder')}
                    loading={false}
                    fullWidth
                  />
                </Stack>
              </FiltersGrid>
            </Stack>
          </FiltersSection>
        )}

        <DataTable<Payment>
          data={paginatedData}
          columns={paymentColumns}
          loading={false}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          emptyMessage={t('payments.noPayments')}
        />
      </Stack>
    </PaymentsContainer>
  );
};

export default Payments;
